<template>
  <v-footer app light padless class="application-footer">
    <IsSaving :anchor="offsetHeight()" :isSaving="isSaving" />
    <v-row class="stepper" no-gutters id="stepperID">
      <v-col cols="12">
        <div class="div-position blue darken-4">
          <v-stepper
            alt-labels
            light
            non-linear
            class="blue darken-4"
            style="border-radius: 0"
          >
            <v-stepper-header>
              <!-- template here to regroup stepper + divider into one v-for loop -->
              <template v-for="step in getUpdatedSteps">
                <v-divider
                  v-show="!!step.index"
                  :class="getDividerColorClass(currentUpdatedIndex, step)"
                  class="my-9"
                  style="border-width: 2px"
                  :key="`${step.key}-divider`"
                />
                <v-stepper-step
                  :key="`${step.key}-step`"
                  :class="{
                    ico: fontItems[step.icon],
                    'selected-step': step.goTo === $route.path,
                  }"
                  editable
                  complete
                  :step="step.index.toString()"
                  :edit-icon="step.icon"
                  :color="getStepColor(currentUpdatedIndex, step)"
                  class="py-4"
                  @click="clickOnStepHandler(step)"
                >
                  <span class="white--text text-caption text-center text-no-wrap">{{
                    $t(`scenario.${step.key}.label`)
                  }}</span>
                </v-stepper-step>
              </template>
            </v-stepper-header>
          </v-stepper>

          <ActionButton
            label="nextBtn"
            data-test="next"
            data-cy="nextStepButton"
            class="next-button"
            v-if="nextIsAvailable"
            @click="clickGoToNext"
          />
        </div>
      </v-col>
    </v-row>
  </v-footer>
</template>
<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import _icon from '@/helpers/iconHelper';
import { StepConfig } from '@/classes/StepConfig';
import _color from '@/helpers/colors';
import IsSaving from '@/components/IsSavingComp.vue';
import ActionButton from '@/components/ActionButton.vue';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { InjectionModelRef } from '@/dto/scenario/ScenarioReference';
import { EditionModeRef } from '@/classes/ScenarioReference';

/**
 * This component displays the current steps configuration in a readable way.
 * It also handle some pages (sizing in particular) specific navigation logic.
 */
@Component({
  components: {
    ActionButton,
    IsSaving
  }
})
export default class Stepper extends Vue {
  private fontItems: any = _icon.customIcons; // TODO : type as MAP

  private isSaving: boolean = false;

  // COMPUTED =======================================================
  get currentUpdatedIndex(): number {
    return this.$store.getters['stepper/currentUpdatedIndex'];
  }

  get getUpdatedSteps(): StepConfig[] {
    return this.$store.getters['stepper/getUpdatedSteps'];
  }
  // getScenarioIsSaving
  get getScenarioIsSaving(): StepConfig[] {
    return this.$store.getters['scenario/getScenarioIsSaving'];
  }

  get EditMode(): EditionModeRef {
    return this.$store.getters['scenario/getEditionMode'];
  }

  get nextIsAvailable(): boolean {
    if (this.isBeforeReports) {
      return !this.criticalFieldError;
    }
    if (this.isCurrentStepLastStep) {
      return false;
    }
    return true;
  }

  get isCurrentStepLastStep(): boolean {
    return this.currentUpdatedIndex === this.getUpdatedSteps.length - 1;
  }

  get criticalFieldError(): boolean {
    return this.$store.getters['scenario/getCriticalFieldErrors'].length > 0;
  }

  // METHODS =======================================================
  get isBeforeReports(): boolean {
    const scenario: ScenarioExt = this.$store.getters['scenario/getFields'];
    if (
      scenario.hasInjectionType(InjectionModelRef.total_injection) &&
      !scenario.isRenFrance
    ) {
      return this.$route.name === 'scenario_step_eco';
    }
    if (scenario.isRenFrance) {
      return this.$route.name === 'scenario_ren_fr_step_structure_finance';
    }
    return this.$route.name === 'scenario_step_sizing';
  }

  private offsetHeight() {
    const id = document.getElementById('stepperID');
    if (id) {
      return id.offsetHeight;
    }
  }

  private getStepColor(index: number, conf: StepConfig): string {
    if (conf.index === index) {
      return 'white';
    }
    if (conf.available === false) {
      return 'grey';
    }
    if (conf.hasErrors) {
      return this.EditMode !== EditionModeRef.creation
        ? _color['vv-red']
        : 'blue';
    }
    return conf.index < index || conf.mustBeControlled ? 'green' : 'blue';
  }

  private getDividerColorClass(index: number, conf: StepConfig): string {
    return conf.index <= index || conf.mustBeControlled ? 'green' : 'blue';
  }

  private clickOnStepHandler(conf: StepConfig) {
    if (conf.available && conf.goTo !== this.$router.currentRoute.path) {
      this.$router.push({ path: conf.goTo });
    }
  }

  /**
   * Control method for the next button - on sizing page, the goToNext will be triggered by eventBus once the report is received.
   */
  private async clickGoToNext() {
    if (this.isBeforeReports) {
      await this.$store.dispatch(
        'scenario/updateScenario',
        this.$store.getters['scenario/getFields']
      ); // TODO should be in the store

      const scenario: ScenarioExt = this.$store.getters['scenario/getFields'];
      if (scenario.isRenFrance) {
        await this.$store.dispatch('scenario/sendScenarioToRenFrAlgorithm');
      } else {
        await this.$store.dispatch('scenario/sendScenarioToAlgorithm');
      }
    }
    this.goToNext();
  }

  /**
   * Search for the next step, then goes to it.
   */
  private goToNext() {
    const nextStep = this.getUpdatedSteps.find(
      (c) => c.index > this.currentUpdatedIndex && c.available
    );
    if (nextStep) {
      this.$router.push({ path: nextStep.goTo });
    }
  }

  // HOOKS =======================================================
  @Watch('getScenarioIsSaving', { immediate: true })
  private handleIsSaving(value: any) {
    setTimeout(
      () => {
        this.isSaving = value;
        // delay the blanketing of the saving div
      },
      value ? 100 : 750
    );
  }
}
/* Note on CSS :
  .save-stateOne/Two, I use bottom property to start
  the transition from the top to the bototm.
  If we change the stepper height, we need to adapt the
  bottom attribute value
*/
</script>
<style lang="stylus">
.application-footer {
  z-index: 5000 !important;
}

#app {
  .v-stepper__header {
    padding: 0 35px;
  }

  .v-stepper--alt-labels .v-stepper__step {
    padding: 0px;
    flex-basis: 100px;
  }
}

.v-stepper__step span {
  top: 0px;
  padding: 21px;
}

.v-stepper__label span {
  padding: 0px;
}

.ico .v-stepper__step__step i {
  font-size: 25px !important;
  font-family: icomoon;
}

.v-stepper__label {
  font-family: Roboto;
}

.selected-step {
  .v-stepper__step__step {
    i {
      color: #0C4398 !important;
    }
  }
}

.div-position {
  position: relative;
}
.next-button {
  position: absolute;
  left: 84.44%;
  top: -170px;
}
</style>
